import { useAtom, useAtomValue } from 'jotai/index';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { hitSlop, Icon, Notice, View, XIcon, XStack, YStack } from 'smoothie';
import { v4 as uuidv4 } from 'uuid';

import { currentlyEditingConfigAtom } from '../../../../../state/PaycheckRoutingAtoms';
import { bankAccountDisplayDetails } from '../../../../../state/SpacesAtoms';
import {
    type PaycheckRoutingRule,
    PaycheckRoutingRuleType,
} from '../../../../../types/PaycheckRouting';
import AddRuleButton from '../../../common/AddRuleButton';
import RuleEditorButtons from '../common/RuleEditorButtons';
import RuleTargetAccount from '../common/RuleTargetAccount';
import RemainingRuleAmount from './RemainingRuleAmount';

interface RemainingRulesEditorProps {
    setIsEditing: (isEditing: boolean) => void;
}

const RemainingRulesEditor = ({ setIsEditing }: RemainingRulesEditorProps) => {
    const [currentlyEditingConfig, setCurrentlyEditingConfig] = useAtom(
        currentlyEditingConfigAtom,
    );
    const bankAccountDetails = useAtomValue(bankAccountDisplayDetails);

    const [modifiedRules, setModifiedRules] = useState<
        Array<Partial<PaycheckRoutingRule>>
    >(currentlyEditingConfig?.rules?.remaining ?? []);
    const [showError, setShowError] = useState(false);

    const allRemainingRulesHaveTargetAccounts = useMemo(() => {
        return modifiedRules.every((rule) => rule.accountId != null);
    }, [modifiedRules]);

    const remainingRulesAddTo100 = useMemo(() => {
        return (
            modifiedRules.reduce((acc, rule) => acc + (rule.amount ?? 0), 0) ===
            100
        );
    }, [modifiedRules]);

    return (
        <YStack gap={20}>
            {modifiedRules.map((rule) => {
                return (
                    <XStack gap={8} alignItems="center" key={rule.id}>
                        <RemainingRuleAmount
                            rule={rule}
                            disabled={!!rule.accountMetadata}
                            onUpdate={(amount) => {
                                setModifiedRules(
                                    modifiedRules.map((modifiedRule) => {
                                        return modifiedRule.id === rule.id
                                            ? {
                                                  ...modifiedRule,
                                                  amount,
                                              }
                                            : modifiedRule;
                                    }),
                                );
                            }}
                        />
                        <RuleTargetAccount
                            cardProps={{
                                padding: 10,
                                borderRadius: 12,
                                flexGrow: 1,
                            }}
                            disabled={!!rule.accountMetadata}
                            iconProps={{ iconSize: 14, padding: 10 }}
                            bankAccounts={bankAccountDetails}
                            rule={rule}
                            updateTargetAccountId={(targetAccountId) => {
                                setModifiedRules(
                                    modifiedRules.map((modifiedRule) => {
                                        return modifiedRule.id === rule.id
                                            ? {
                                                  ...modifiedRule,
                                                  accountId: targetAccountId,
                                              }
                                            : modifiedRule;
                                    }),
                                );
                            }}
                            openSelect={() => {}}
                            isEditingRemainingRules={true}
                        />
                        {modifiedRules.length > 1 && (
                            <View
                                animation="button"
                                pressStyle={{ scale: 0.95 }}
                                onPress={() => {
                                    setModifiedRules(
                                        modifiedRules.filter((r) => {
                                            return r.id !== rule.id;
                                        }),
                                    );
                                }}
                                {...hitSlop(8)}
                            >
                                <Icon icon={XIcon} size={16} color="$gray900" />
                            </View>
                        )}
                    </XStack>
                );
            })}
            <AddRuleButton
                onPress={() => {
                    if (!bankAccountDetails[0]) return; // TODO probably log something here

                    setModifiedRules([
                        ...modifiedRules,
                        {
                            id: uuidv4(),
                            type: PaycheckRoutingRuleType.PERCENTAGE,
                        },
                    ]);
                }}
            />
            {showError && (
                <Notice
                    variant="danger"
                    title="Error"
                    description={
                        !remainingRulesAddTo100
                            ? 'Please ensure percentages add up to 100%'
                            : 'Please select an account for each rule'
                    }
                />
            )}
            <RuleEditorButtons
                onSave={() => {
                    setShowError(false);

                    const areRulesValid =
                        allRemainingRulesHaveTargetAccounts &&
                        remainingRulesAddTo100;
                    if (!areRulesValid) {
                        setShowError(true);
                        return;
                    }

                    const filteredRules = modifiedRules.filter((rule) => {
                        return (rule.amount ?? 0) > 0;
                    });

                    setCurrentlyEditingConfig({
                        ...currentlyEditingConfig,
                        rules: {
                            primary:
                                currentlyEditingConfig?.rules?.primary ?? [],
                            remaining: filteredRules as PaycheckRoutingRule[],
                        },
                    });
                    setIsEditing(false);
                }}
                onCancel={() => {
                    setIsEditing(false);
                }}
            />
        </YStack>
    );
};

export default RemainingRulesEditor;
